import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck } from "lucide-react";

const INCLUDES = [
  "Your full 105-statement Authority Portrait across seven dimensions",
  "Your Ground, Ghosts, Shifts and Signals",
  "Areas for Reclamation, questions and practical experiments",
  "The C³ framework for conscious choice",
  "A downloadable PDF report, yours to keep",
];

export default function Paywall() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const giftCode = params.get("code");
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoErr, setPromoErr] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    api.get("/me/assessment-access")
      .then((r) => { if (r.data.has_access) navigate("/consent", { replace: true }); else setChecking(false); })
      .catch(() => setChecking(false));
  }, [navigate]);

  const applyPromo = async () => {
    setPromoErr(""); setPromoBusy(true);
    try {
      await api.post("/me/redeem-promo", { code: promoCode.trim() });
      toast.success("Promo verified. Your assessment is unlocked.");
      navigate("/consent", { replace: true });
    } catch (e) {
      setPromoErr(formatApiError(e.response?.data?.detail));
    } finally { setPromoBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "assessment_access", origin_url: window.location.origin, promo_code: giftCode || undefined });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (checking) return (<div><HeaderNav /><div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div></div>);

  return (
    <div>
      <HeaderNav />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-14 pb-24 cg-fade-up" data-testid="paywall-page">
        <p className="cg-eyebrow mb-3">Unlock your assessment</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6] mb-4">Your Authority Portrait</h1>
        <p className="text-[#9CA3AF] leading-relaxed max-w-xl">
          One payment unlocks the full Caesar's Ghost assessment and your personal Authority Portrait. Take it whenever you are ready, and return any time to your results.
        </p>

        <div className="cg-card p-8 mt-8" data-testid="paywall-card">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-5xl text-[#F3F4F6]" data-testid="paywall-price">$59.95</span>
            <span className="cg-eyebrow">CAD · one time</span>
          </div>
          {giftCode && (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#7D9D86] bg-[rgba(125,157,134,0.1)] border border-[rgba(125,157,134,0.3)] rounded-full px-4 py-1.5" data-testid="paywall-gift-note">
              <Check className="w-4 h-4" /> Complimentary access code applied at checkout
            </div>
          )}
          <ul className="mt-6 space-y-3">
            {INCLUDES.map((x) => (
              <li key={x} className="flex gap-3 text-[#B9BDC7] leading-relaxed">
                <Check className="w-5 h-5 text-brass shrink-0 mt-0.5" /> <span>{x}</span>
              </li>
            ))}
          </ul>
          <button data-testid="paywall-pay-button" onClick={pay} disabled={busy}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brass text-obsidian font-medium px-7 py-3.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to secure checkout…</> : "Continue to Payment"}
          </button>
          <p className="mt-4 text-xs text-[#6B7280] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brass" /> Secure payment by Stripe. Taxes handled at checkout.
          </p>

          <div className="mt-6 pt-6 border-t border-[var(--cg-border)]" data-testid="promo-section">
            <p className="text-sm text-[#B9BDC7] mb-3 font-medium">Have a promo code?</p>
            <label className="text-xs text-[#6B7280]">Email address</label>
            <input value={user?.email || ""} disabled data-testid="promo-email"
              className="mt-1 mb-3 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#6B7280] font-mono" />
            <label className="text-xs text-[#6B7280]">Promo code</label>
            <input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} data-testid="promo-code-input" placeholder="Enter your promo code"
              className="mt-1 w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2.5 text-sm text-[#F3F4F6] font-mono uppercase focus:border-brass outline-none" />
            {promoErr && <p data-testid="promo-error" className="text-sm text-terracotta mt-2">{promoErr}</p>}
            <button data-testid="promo-apply-button" onClick={applyPromo} disabled={promoBusy || !promoCode.trim()}
              className="mt-3 inline-flex items-center gap-2 border border-brass text-brass px-5 py-2.5 rounded-full text-sm cg-scale-btn hover:bg-[rgba(217,162,95,0.1)] disabled:opacity-40">
              {promoBusy ? "Verifying…" : "Apply Promo Code"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
